import React, { useState, useRef } from 'react';
import { Upload, Loader2, FileVideo, X, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SegmentationMode } from '@/lib/api/contentLibrary';

interface VideoUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (data: {
    file: File;
    title?: string;
    customInstructions?: string;
    metadataInstructions?: string;
    segmentationMode: SegmentationMode;
    playlistPolicy: 'mixable' | 'standalone';
    playlistGroup?: string;
  }) => Promise<void>;
  isUploading?: boolean;
}

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB
const MAX_METADATA_INSTRUCTIONS_LENGTH = 2000;

export const VideoUploadDialog: React.FC<VideoUploadDialogProps> = ({
  open,
  onOpenChange,
  onUpload,
  isUploading = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [metadataInstructions, setMetadataInstructions] = useState('');
  const [segmentationMode, setSegmentationMode] = useState<SegmentationMode>('auto');
  const [playlistPolicy, setPlaylistPolicy] = useState<'mixable' | 'standalone'>('mixable');
  const [playlistGroup, setPlaylistGroup] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setFile(null);
    setTitle('');
    setCustomInstructions('');
    setMetadataInstructions('');
    setSegmentationMode('auto');
    setPlaylistPolicy('mixable');
    setPlaylistGroup('');
    setError(null);
  };

  const handleClose = () => {
    if (!isUploading) {
      resetForm();
      onOpenChange(false);
    }
  };

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload MP4, MOV, AVI, or WebM files.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds 1GB limit.';
    }
    return null;
  };

  const handleFileSelect = (selectedFile: File) => {
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setFile(selectedFile);
    // Auto-fill title from filename if empty
    if (!title) {
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
      setTitle(nameWithoutExt);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a capture file.');
      return;
    }

    try {
      await onUpload({
        file,
        title: title.trim() || undefined,
        customInstructions: customInstructions.trim() || undefined,
        metadataInstructions: metadataInstructions.trim() || undefined,
        segmentationMode,
        playlistPolicy,
        playlistGroup: playlistGroup.trim() || undefined,
      });
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="px-6 pb-4 pt-6">
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Capture
          </DialogTitle>
          <DialogDescription>
            Add a recorded demo capture. Hobbes will segment it and stage the results for review.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <div className="space-y-4">
              {/* File Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`
                  cursor-pointer rounded-lg border-2 border-dashed p-5 text-center
                  transition-colors
                  ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'}
                  ${file ? 'bg-muted/50' : ''}
                `}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_VIDEO_TYPES.join(',')}
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0];
                    if (selectedFile) handleFileSelect(selectedFile);
                  }}
                  className="hidden"
                />

                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileVideo className="h-8 w-8 text-primary" />
                    <div className="min-w-0 text-left">
                      <p className="max-w-[220px] truncate font-medium">{file.name}</p>
                      <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto mb-2 h-9 w-9 text-muted-foreground" />
                    <p className="text-sm font-medium">Drop a capture file here</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      or browse for MP4, MOV, AVI, or WebM up to 1GB
                    </p>
                  </>
                )}
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Capture title (optional)</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Admin setup walkthrough"
                />
              </div>

              <div className="space-y-4 rounded-lg border bg-card p-4">
                <div className="space-y-1">
                  <h3 className="text-sm font-medium">Segment grouping</h3>
                  <p className="text-xs text-muted-foreground">
                    Keep generated segments together, or allow them to mix with related captures.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="playlistPolicy">Mixing rule</Label>
                  <Select
                    value={playlistPolicy}
                    onValueChange={(value: 'mixable' | 'standalone') => setPlaylistPolicy(value)}
                  >
                    <SelectTrigger id="playlistPolicy">
                      <SelectValue placeholder="Select a policy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mixable">Can mix within a group</SelectItem>
                      <SelectItem value="standalone">Keep this capture standalone</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {playlistPolicy === 'standalone'
                      ? 'Standalone segments are only used with this capture.'
                      : 'Mixable segments can be combined with captures in the same group.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="playlistGroup">Segment Group (optional)</Label>
                  <Input
                    id="playlistGroup"
                    value={playlistGroup}
                    onChange={(e) => setPlaylistGroup(e.target.value)}
                    placeholder="e.g., product-overview"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use a shared group name for captures that belong in the same playlist lane.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2 rounded-lg border bg-card p-4">
              <Label htmlFor="segmentationMode">Processing</Label>
              <Select
                value={segmentationMode}
                onValueChange={(value) => setSegmentationMode(value as SegmentationMode)}
              >
                <SelectTrigger id="segmentationMode">
                  <SelectValue placeholder="Choose processing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-segment and stage</SelectItem>
                  <SelectItem value="none">Upload only for manual clipping</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {segmentationMode === 'none'
                  ? 'Use this when you want to mark the segment boundaries yourself.'
                  : 'Hobbes will split the capture and stage the generated segments for review.'}
              </p>
            </div>

            {/* Processing Guidance */}
            <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
              <h3 className="text-sm font-medium">Processing guidance</h3>
              <p className="text-sm text-muted-foreground">
                Optional notes for how this capture should be split and described.
              </p>

              {/* Segment Instructions */}
              <div className="space-y-2 pt-1">
                <Label htmlFor="customInstructions" className="text-sm">
                  Segment instructions (optional)
                </Label>
                <Textarea
                  id="customInstructions"
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="e.g., Skip setup chatter, split at each feature handoff, and keep pricing as its own segment."
                  rows={4}
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Use this for segment boundaries and the moments that matter.
                </p>
              </div>

              <div className="space-y-2 pt-1">
                <Label htmlFor="metadataInstructions" className="text-sm">
                  Metadata instructions (optional)
                </Label>
                <Textarea
                  id="metadataInstructions"
                  value={metadataInstructions}
                  onChange={(e) => setMetadataInstructions(e.target.value.slice(0, MAX_METADATA_INSTRUCTIONS_LENGTH))}
                  placeholder="e.g., Use customer-facing labels, call this workflow pipeline review, and keep summaries concise."
                  rows={5}
                  maxLength={MAX_METADATA_INSTRUCTIONS_LENGTH}
                  className="text-sm"
                />
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    Use this for labels, summaries, tags, and terminology.
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {metadataInstructions.length}/{MAX_METADATA_INSTRUCTIONS_LENGTH}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t bg-background px-6 py-4">
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!file || isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload Capture
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoUploadDialog;
